import React, { useState, useEffect, useRef } from 'react'
import { t } from 'ttag'
import { Box, CircularProgress } from '@material-ui/core'
import YumDialog from '../../../../components/YumDialog/YumDialog'
import YumDialogTitle from '../../../../components/YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../../../../components/YumDialogContent/YumDialogContent'
import YumDialogContentText from '../../../../components/YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../../../../components/YumDialogActions/YumDialogActions'
import YumCancelButton from '../../../../components/YumCancelButton/YumCancelButton'
import YumSubmitButton from '../../../../components/YumSubmitButton/YumSubmitButton'
import YumInput from '../../../../components/YumInput/YumInput'
import { CompanyId, GenerateTeams$Request } from '../../../../services/api.types'
import { ReactComponent as OrganizerInactiveIcon } from '../../../../assets/v3/icons/organizer-inactive.svg'
import { ReactComponent as GroupActiveIcon } from '../../../../assets/v3/icons/group-active.svg'

interface GenerateTeamsProps {
  open: boolean
  onClose: () => void
  onSubmit: (payload: GenerateTeams$Request) => void
  companyId: CompanyId
  loading: boolean
}

const GenerateTeams: React.FC<GenerateTeamsProps> = ({ open, onClose, onSubmit, companyId, loading }) => {
  const [teamsToGenerate, setTeamsToGenerate] = useState<number | string>(1)
  const isSubmittingRef = useRef(false)

  // Close dialog when request completes
  useEffect(() => {
    // If we were loading and now we're not, and we had submitted the form, close the dialog
    if (!loading && isSubmittingRef.current) {
      isSubmittingRef.current = false
      onClose()
    }
  }, [loading, onClose])

  const handleTeamsToGenerateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10)
    if (!isNaN(value) && value >= 1) {
      if (value > 1000) {
        setTeamsToGenerate(1000)
      } else {
        setTeamsToGenerate(value)
      }
    } else {
      setTeamsToGenerate('') // Default to empty if invalid input
    }
  }

  const isValidInput = () => {
    return typeof teamsToGenerate === 'number' && teamsToGenerate >= 1 && teamsToGenerate <= 1000
  }

  const handleSubmit = () => {
    // Validate input before submitting
    if (!isValidInput()) {
      // Default to 1 if invalid input
      setTeamsToGenerate(1)
      return
    }

    // Mark that we've submitted the form
    isSubmittingRef.current = true

    onSubmit({
      companyId,
      amount: teamsToGenerate as number,
    })
  }

  return (
    <YumDialog open={open}>
      <YumDialogTitle handleClose={onClose}>{t`Generate teams`}</YumDialogTitle>
      <YumDialogContent>
        <YumDialogContentText style={{ marginBottom: '16px' }}>
          {t`How many teams would you like to generate?`}
        </YumDialogContentText>
        <YumInput
          startIcon={GroupActiveIcon}
          autoFocus
          id="teamsToGenerate"
          label={t`Number of teams`}
          fullWidth
          value={teamsToGenerate}
          onChange={handleTeamsToGenerateChange}
          onWheel={(e: { target: { blur: () => any } }) => e.target.blur()}
          type="number"
          inputProps={{ inputMode: 'numeric', min: 1, max: 1000 }}
          disabled={loading}
          style={{ marginBottom: 0 }}
        />
      </YumDialogContent>
      <YumDialogActions>
        <YumCancelButton onClick={onClose} disabled={loading} />
        <YumSubmitButton
          text={t`Generate`}
          onClick={handleSubmit}
          disabled={!isValidInput() || loading}
          submitInProgress={loading}
          immersiveLoader={true}
        />
      </YumDialogActions>
    </YumDialog>
  )
}

export default GenerateTeams
